<script setup lang="ts">
import PrizeCard from '@/components/PrizeCard.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { formatNumberFullTruncate } from '@/helpers/utils'
import StrokeCoinImage from '@/assets/images/icons/gold-coin-icon-s3.webp'
import {
    ECOMMERCE_ITEM_BENEFIT_TYPE,
    ECOMMERCE_ITEM_TAG,
    type EcommerceItem,
    type EcommerceItemBenefit,
} from '@/types/shared/player-config'
import { computed } from 'vue'
import { useShop, useShopItem } from '@/composables/game/shop'
import BestOfferBadgeImage from '@/assets/images/periodic/best-offer-badge2.png'
import PopularOfferBadgeImage from '@/assets/images/shop-popular-badge.png'
import { CARD_COLORS } from '@/enums/common'
import { twMerge } from 'tailwind-merge'
import { usePlayer } from '@/composables/game/player'
import PHP2xImage from '@/assets/images/prize-cards/php2x.webp'
import SpinImage from '@/assets/images/fortune-wheel.webp'
import { allSpecialCurrenciesConfig } from '@/config/special-currencies'

const props = withDefaults(defineProps<{
	item: EcommerceItem
	onlyVisual?: boolean
	showBadge?: boolean
	firstBenefitType?: ECOMMERCE_ITEM_BENEFIT_TYPE
}>(), {
  firstBenefitType: ECOMMERCE_ITEM_BENEFIT_TYPE.COIN,

})

defineEmits<{
	(e: 'selectItem'): void
}>()

const {
  getItemDisabledSeconds,
} = useShop()

const { goldenCoinValue } = usePlayer()
const {
  itemStarPrice,
} = useShopItem(computed(() => props.item))
const benefitCount = computed(() => props.item.benefits.length)
const firstBenefit = computed(() => props.item.benefits.find(b => b.type === props.firstBenefitType) ?? props.item.benefits[0])
const otherBenefits = computed(() => props.item.benefits.filter(b => b.type !== firstBenefit.value.type || b.amount !== firstBenefit.value.amount))

const benefitConfig = computed((): Partial<Record<ECOMMERCE_ITEM_BENEFIT_TYPE, {
	image: string,
	getValue: (benefit: EcommerceItemBenefit) => string
}>> => ({
  [ECOMMERCE_ITEM_BENEFIT_TYPE.COIN]: {
    image: StrokeCoinImage,
    getValue: (benefit: EcommerceItemBenefit) => formatNumberFullTruncate(benefit.amount, false, false, benefit === firstBenefit.value),
  },
  [ECOMMERCE_ITEM_BENEFIT_TYPE.GOLDEN_COIN]: {
    image: StrokeCoinImage,
    getValue: (benefit: EcommerceItemBenefit) => formatNumberFullTruncate(benefit.amount * goldenCoinValue.value, false, false, benefit === firstBenefit.value),
  },
  [ECOMMERCE_ITEM_BENEFIT_TYPE.SPIN]: {
    image: SpinImage,
    getValue: (benefit: EcommerceItemBenefit) => formatNumberFullTruncate(benefit.amount, false, false, benefit === firstBenefit.value),
  },
  [ECOMMERCE_ITEM_BENEFIT_TYPE.PROFIT_X2]: {
    image: PHP2xImage,
    getValue: (benefit: EcommerceItemBenefit) => benefit.amount + 'H',
  },
  [ECOMMERCE_ITEM_BENEFIT_TYPE.OFFLINE_PROFIT]: {
    image: '',
    getValue: (benefit: EcommerceItemBenefit) => `${benefit.amount}H Offline Profit`,
  },
  [ECOMMERCE_ITEM_BENEFIT_TYPE.VIRTUAL_FRIEND]: {
    image: '',
    getValue: (benefit: EcommerceItemBenefit) => benefit.amount + ' Virtual Friends',
  },
  [ECOMMERCE_ITEM_BENEFIT_TYPE.SPECIAL_CURRENCY]: {
    image: '',
    getValue: (benefit: EcommerceItemBenefit) => formatNumberFullTruncate(benefit.amount, false, false, benefit === firstBenefit.value),
  },
}))

function getBenefitImage(benefit: EcommerceItemBenefit) {
  if (benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.SPECIAL_CURRENCY) {
    return allSpecialCurrenciesConfig.value[benefit.currency]?.image
  }
  return benefitConfig.value[benefit.type]?.image
}
</script>
<template>
  <div class="relative flex">
    <PrizeCard
      card-class="border-[#545351]"
      :class="onlyVisual && 'pointer-events-none'"
      :color="CARD_COLORS.GRAY"
      :disabled="getItemDisabledSeconds(item) > 0 && !onlyVisual"
      @click="$emit('selectItem')"
    >
      <div
        v-if="showBadge && item.tags.includes(ECOMMERCE_ITEM_TAG.POPULAR)"
        class="absolute -left-8 -top-8 rotate-[-23deg] size-[100px] z-[1]"
      >
        <img
          :src="PopularOfferBadgeImage"
          class="w-full h-auto float"
        >
      </div>
      <div
        :class="twMerge('flex h-full w-full flex-col items-center justify-between',
                        onlyVisual && 'justify-center gap-y-3',
        )"
      >
        <div class="flex items-center gap-x-1">
          <img
            v-if="benefitCount > 1 && getBenefitImage(firstBenefit)"
            :src="getBenefitImage(firstBenefit)"
            class="h-[54px] w-auto object-cover"
          >
          <span
            class="black-stroke text-[42px] font-semibold leading-[64px] -tracking-[2.4px] text-white"
          >
            {{ formatNumberFullTruncate(firstBenefit.amount, false, false, true) }}
          </span>
        </div>

        <template v-if="benefitCount === 1 && getBenefitImage(firstBenefit)">
          <img
            :src="getBenefitImage(firstBenefit)"
            class="h-[100px] -mt-[10px] w-auto object-cover"
          >
          <div
            v-if="item.tags.includes(ECOMMERCE_ITEM_TAG.BEST_OFFER)"
            class="absolute pointer-events-none z-[3] h-auto w-[124%] bottom-10"
          >
            <img
              :src="BestOfferBadgeImage"
              class="h-auto w-full object-cover"
            >
          </div>
        </template>
        <div
          v-else
          class="flex w-full flex-col items-center -mt-4"
        >
          <div
            v-for="(benefit, index) in otherBenefits"
            :key="index"
            class="flex items-center gap-x-1"
            :class="{'-mt-1': index > 0 }"
          >
            <img
              v-if="getBenefitImage(benefit)"
              :src="getBenefitImage(benefit)"
              class="h-6 object-cover"
            >
            <span
              class="black-stroke text-[30px] font-semibold leading-[40px] -tracking-[2.4px] text-white"
            >{{ benefitConfig[benefit.type]?.getValue(benefit)
            }}</span>
          </div>
          <div
            v-if="item.tags.includes(ECOMMERCE_ITEM_TAG.BEST_OFFER)"
            class="pointer-events-none z-[3] -mb-5 h-auto w-[124%]"
          >
            <img
              :src="BestOfferBadgeImage"
              class="h-auto w-full object-cover"
            >
          </div>
        </div>
        <div
          v-if="!onlyVisual"
          class="flex w-full flex-col"
        >
          <BaseButton
            style-type="secondary"
            size-style="small"
            class="!h-[60px] !min-h-[60px] w-full"
          >
            <div class="flex items-center gap-x-1 text-[27px] leading-[38px]">
              <span>{{ `$${itemStarPrice.usdAmount.toFixed(2)}` }}</span>
            </div>
          </BaseButton>
        </div>
      </div>
    </PrizeCard>
  </div>
</template>
<style>
@import '@/assets/styles/sales.scss';

.black-stroke {
	text-shadow: -1.5px -1.5px 0 #000,
	1.5px -1.5px 0 #000,
	-1.5px 1.5px 0 #000,
	1.5px 1.5px 0 #000,
	-2px 0 0 #000,
	2px 0 0 #000,
	0 -2px 0 #000,
	0 2px 0 #000,
	-1px -1px 0 #000, /* Finer shadow adjustments */ 1px -1px 0 #000,
	-1px 1px 0 #000,
	1px 1px 0 #000;
}
</style>
